import { useStateMachine } from 'little-state-machine';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaInfoCircle } from 'react-icons/fa';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormGroup } from '../../../../Components/FormGroup/FormGroup';
import { fetchWrapper } from '../../../../_helpers/fetchWrapper';
import {
  convertToNumber,
  debounce,
  ErrorMessageHandler,
  getGenderLabel,
  isNumeric,
  onlyNotSpecialCharacters,
  verifyEmail
} from '../../../../_helpers/_methods';
import NavigationButtonGroup from '../../../admin/Events/Steps/components/NavigationButtonGroup';
import PhysicalLocationDetails from '../../../admin/Events/Steps/components/PhysicalLocationDetails';
import updateAction from '../updateAction';

function TraineeStep0() {
  const lang = useSelector((state) => state?.local?.local);
  const { actions } = useStateMachine({ updateAction });
  const { state } = useStateMachine({ updateAction });
  const navigate = useNavigate();
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPhoneInValid, setIsPhoneInValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { id, edit: isEdit } = useParams();
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,

    // setError,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {}
  });
  const location = useLocation();
  const trainee = useSelector((state) => state.trainees.trainees);

  const onSubmit = async (data) => {
    setIsLoading(true);
    if (!(await validatePhone({ phone: data.mobile_number }))) {
      setIsLoading(false);

      return;
    }
    if (!(await validateEmail({ email: data.email }))) {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    actions.updateAction(data);

    let path = location.pathname.split('/');
    if (isEdit) {
      navigate(`/admin/trainees/${id}/edit/1`);
    } else {
      path.includes('admin') ? navigate('/admin/users/trainees/1') : navigate('/trainees/apply/1');
    }
  };

  const handleTraineeGender = (e) => {
    setValue('trainee_gender', e);
    clearErrors('trainee_gender');
  };

  const handleCasteGroup = (e) => {
    setValue('trainee_caste_group', e);
    clearErrors('trainee_caste_group');
  };

  useEffect(() => {
    setValue(`trainee_caste_group`, state?.trainee_caste_group);
    setValue(`trainee_gender`, state?.trainee_gender);
  }, []);

  const [options, setOptions] = useState([]);

  useEffect(() => {
    const getCasteGroupData = async () => {
      try {
        const response = await fetchWrapper.get('/ethenicity/', true);
        let data = response.data.results.map((item) => {
          return {
            id: item.id,
            label: item.title,
            value: item.title,
            options: item.childrens.length
              ? item.childrens.map((child) => {
                  return { label: `${child.title} (${item.title})`, value: child.id, id: child.id };
                })
              : [{ label: item.title, value: item.title, id: item.id }]
          };
        });
        setOptions(data);
      } catch (error) {
        ErrorMessageHandler(error);
      }
    };
    getCasteGroupData();
  }, []);

  useEffect(() => {
    if (isEdit && trainee) {
      setValue('fullname', state.fullname || trainee.user?.fullname);
      setValue('mobile_number', state.mobile_number || trainee.user?.phone);
      setValue('email', state.email || trainee.user?.email);
      setValue(
        'trainee_gender',
        state.trainee_gender || { label: trainee.user?.gender, value: trainee.user?.gender }
      );
      setValue(
        'trainee_caste_group',
        state.trainee_caste_group || {
          id: trainee?.ethenicity?.id,
          label: trainee?.ethenicity?.title,
          value: trainee?.ethenicity?.title
        }
      );
      setValue('trainee_disability', state.trainee_disability || `${trainee?.disability}`);
    }
  }, [trainee]);

  const handlePhoneCheck = async (e) => {
    let body = { phone: e.target.value };
    clearErrors('mobile_number');

    body.phone.length === 0 ? setIsPhoneInValid(false) : await validatePhone(body);
  };

  const validatePhone = async (body) => {
    try {
      if (isEdit) {
        if (trainee.user.phone !== body.phone) {
          await fetchWrapper.post('/validate-phone/', body);
          clearErrors('mobile_number');
          setIsPhoneInValid(false);
        }
        return true;
      } else {
        await fetchWrapper.post('/validate-phone/', body);
        clearErrors('mobile_number');
        setIsPhoneInValid(false);
        return true;
      }
    } catch (error) {
      setIsPhoneInValid(
        error.error[0]
          ? error.error[0]
          : lang === 'ne'
          ? error.error.non_field_error_np
          : error.error.non_field_error
      );
      return false;
    }
  };

  const handleEmailCheck = async (e) => {
    clearErrors('email');
    setIsEmailValid(false);
    let body = { email: e.target.value };

    if (body.email.lenght < 2) {
      setIsEmailValid(false);
    } else {
      if (verifyEmail(body.email)) {
        await validateEmail(body);
      } else {
        // if (body.email.lenght === 0) {
        setIsEmailValid(intl.formatMessage({ id: 'email_validate_message' }));
        // }
        // clearErrors('email');
        // setIsEmailValid(true);
      }
    }
  };
  const validateEmail = async (body) => {
    try {
      if (isEdit) {
        if (trainee.user?.email !== body.email) {
          await fetchWrapper.post('/validate-email/', body);
          clearErrors('email');
          setIsEmailValid(false);
        }
        return true;
      } else {
        await fetchWrapper.post('/validate-email/', body);
        clearErrors('email');
        setIsEmailValid(false);
        return true;
      }
    } catch (error) {
      setIsEmailValid(
        lang === 'ne' ? error.error?.non_field_error_np : error.error?.non_field_error
      );
      return false;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="alert alert-info d-flex justify-content-inline mb-0">
        <div className="d-flex align-items-center">
          <FaInfoCircle size={50} className="me-3" />
          <FormattedMessage id="registration.participantWelcome" />
        </div>
      </div>
      <div className="step-0 py-3">
        <div className="row">
          <h3 className="my-2">
            <FormattedMessage id="personal_details" />
          </h3>
          <div className="col-md-3">
            <FormattedMessage id="fullname">
              {(id) => (
                <FormGroup
                  type="text"
                  placeholder={id[0]}
                  register={register}
                  error={errors}
                  id="fullname"
                  onChange={(e) => {
                    e.target.value = onlyNotSpecialCharacters(e.target.value);
                  }}
                  defaultValue={state?.fullname || (isEdit && trainee.user?.fullname)}
                  label={<FormattedMessage id="fullname" />}
                  required={true}
                  errorId={'fullname'}
                />
              )}
            </FormattedMessage>
          </div>
          <div className="col-md-2">
            <FormattedMessage id="mobile_number">
              {(placeholder) => (
                <FormGroup
                  type="text"
                  register={register}
                  placeholder={placeholder[0]}
                  min={0}
                  max={10}
                  error={errors}
                  id="mobile_number"
                  label={<FormattedMessage id="mobile_number" />}
                  onChange={(e) => {
                    if (isNumeric(e.target.value)) {
                      if (e.target.value.length === 0 || 1 > e.target.value.length < 10)
                        debounce(handlePhoneCheck, 1000)(e);
                    } else {
                      e.target.value = convertToNumber(e.target.value);
                      if (e.target.value.length === 0 || 1 > e.target.value.length < 10)
                        debounce(handlePhoneCheck, 1000)(e);
                    }
                  }}
                  errorId={`mobile_number`}
                  required={true}
                  defaultValue={state?.mobile_number || (isEdit && trainee.user?.phone)}
                />
              )}
            </FormattedMessage>
            {isPhoneInValid ? (
              <div className="ms-2">
                <small className="text-danger ">{isPhoneInValid}</small>
              </div>
            ) : null}
          </div>
          <div className="col-md-3">
            <FormattedMessage id="email">
              {(placeholder) => (
                <FormGroup
                  id="email"
                  type="text"
                  placeholder={placeholder[0]}
                  register={register}
                  onChange={(e) => {
                    if (e.target.value.length === 0) {
                      setIsEmailValid(false);
                    } else {
                      // setError('email', '');
                      debounce(handleEmailCheck, 1000)(e);
                    }
                  }}
                  error={errors}
                  label={<FormattedMessage id="email" />}
                  defaultValue={state?.email || (isEdit && trainee.user?.email)}
                  errorId={`email`}
                  required={true}
                />
              )}
            </FormattedMessage>
            {isEmailValid ? (
              <div className="ms-2">
                <small className="text-danger">{isEmailValid}</small>
              </div>
            ) : null}
          </div>
          <div className="col-md-2">
            <FormattedMessage id="select_gender">
              {(placeholder) => (
                <FormGroup
                  id="trainee_gender"
                  type="select"
                  placeholder={placeholder[0]}
                  register={register}
                  error={errors}
                  onChange={handleTraineeGender}
                  options={[
                    {
                      label: lang === 'ne' ? 'पुरुष' : 'Male',
                      value: 'male'
                    },
                    {
                      label: lang === 'ne' ? 'महिला' : 'Female',
                      value: 'female'
                    },
                    {
                      label: lang === 'ne' ? 'यौनिक तथा लैङ्गिक अल्पसंख्यक' : 'Other',
                      value: 'other'
                    }
                  ]}
                  defaultValue={
                    state?.trainee_gender ||
                    (isEdit &&
                      trainee.user && {
                        label: getGenderLabel(trainee.user?.gender, lang),
                        value: trainee.user?.gender
                      })
                  }
                  label={<FormattedMessage id="gender" />}
                  errorId={`gender`}
                  required={true}
                />
              )}
            </FormattedMessage>
          </div>
          <div className="col-md-2">
            <FormattedMessage id="select_caste_group">
              {(placeholder) => (
                <FormGroup
                  id="trainee_caste_group"
                  type="select"
                  placeholder={placeholder[0]}
                  register={register}
                  error={errors}
                  onChange={handleCasteGroup}
                  options={options}
                  defaultValue={
                    state?.trainee_caste_group ||
                    (isEdit && {
                      label: trainee.ethenicity?.title,
                      value: trainee.ethenicity?.id
                    })
                  }
                  label={<FormattedMessage id="caste/group" />}
                />
              )}
            </FormattedMessage>
          </div>
          <div className="col-md-3">
            <FormGroup
              id="trainee_disability"
              type="radio"
              register={register}
              error={errors}
              options={[
                {
                  label: lang === 'ne' ? 'छ' : 'Yes',
                  value: 'true',
                  name: 'trainee_disability',

                  id: 'trainee_disability_yes'
                },
                {
                  value: 'false',
                  label: lang === 'ne' ? 'छैन' : 'No',
                  id: 'trainee_disability_no',
                  name: 'trainee_disability'
                }
              ]}
              defaultValue={state.trainee_disability}
              label={<FormattedMessage id="any_disability" />}
            />
          </div>
        </div>
        <h3 className="my-2">
          <FormattedMessage id="permanent_address" />
        </h3>
        <PhysicalLocationDetails
          id="trainees_address"
          isEdit={isEdit}
          type="trainee"
          register={register}
          errors={errors}
          clearErrors={clearErrors}
          setValue={setValue}
        />
      </div>
      <div className="registration-footer">
        <hr />
        <NavigationButtonGroup
          hasPrevious={false}
          isLoading={isLoading}
          backTo={
            isEdit
              ? '/admin/users/trainees/list'
              : location.pathname.split('/').includes('admin')
              ? '/admin/users/trainees/list'
              : ''
          }
          // showSubmit={id}
        />
      </div>
    </form>
  );
}

export default TraineeStep0;
